import type { SajuAnalysis } from '../types/input';
import type { GyeokGukCompat } from '../types/compatibility';

/**
 * 격국 카테고리 간 십신 흐름 기반 시너지 판정.
 *
 * 십신 생극 흐름:
 * - 식상 → 재성 (식상생재): 아이디어가 부를 창출
 * - 재성 → 관성 (재생관): 부가 권위를 뒷받침
 * - 관성 → 인성 (관인상생): 권위가 학문/지혜를 키움
 * - 인성 → 비겁 → 식상: 순환 완성
 */

// 격국을 십신 카테고리로 매핑 (비겁 포함 5원소 순환)
type SipsinCategory = '비겁' | '식상' | '재성' | '관성' | '인성';

// 종격(종아/종재/종살/종강)과 국(곡직/염상/가색/종혁/윤하)은
// 일반 십신 흐름이 적용되지 않으므로 의도적으로 매핑하지 않음 → synergy='중립'
const TYPE_TO_CATEGORY: Record<string, SipsinCategory | undefined> = {
  '건록격': '비겁', '양인격': '비겁',
  '식신격': '식상', '상관격': '식상',
  '편재격': '재성', '정재격': '재성',
  '편관격': '관성', '정관격': '관성',
  '편인격': '인성', '정인격': '인성',
};

// 십신 상생 흐름 (A → B = A가 B를 생함)
const FLOW: Record<SipsinCategory, SipsinCategory> = {
  '비겁': '식상',
  '식상': '재성',
  '재성': '관성',
  '관성': '인성',
  '인성': '비겁',
};

// 십신 상극 흐름 (A가 B를 극함)
const CLASH: Record<SipsinCategory, SipsinCategory> = {
  '비겁': '재성',
  '식상': '관성',
  '재성': '인성',
  '관성': '비겁',
  '인성': '식상',
};

/**
 * 두 사주의 격국 호환성을 분석한다.
 */
export function analyzeGyeokGukCompat(
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): GyeokGukCompat {
  const gk1 = analysis1.gyeokGuk;
  const gk2 = analysis2.gyeokGuk;

  const cat1 = TYPE_TO_CATEGORY[gk1.type];
  const cat2 = TYPE_TO_CATEGORY[gk2.type];

  let synergy: GyeokGukCompat['synergy'] = '중립';

  if (cat1 && cat2) {
    if (FLOW[cat1] === cat2 || FLOW[cat2] === cat1) {
      // 한쪽이 다른쪽을 생하는 흐름
      synergy = '상생흐름';
    } else if (cat1 === cat2) {
      // 같은 카테고리 = 상호보완 (비화 관계)
      synergy = '상호보완';
    } else {
      // 상극 관계 판정 (CLASH 맵 기반)
      const isClash = CLASH[cat1] === cat2 || CLASH[cat2] === cat1;
      synergy = isClash ? '충돌' : '중립';
    }
  }

  // 파격이면 시너지 효과 감소 (격국 구조가 깨져 있으므로)
  if (gk1.status === '파격' || gk2.status === '파격') {
    if (synergy === '상생흐름') synergy = '상호보완';
    else if (synergy === '상호보완') synergy = '중립';
  }

  const synergyLabel: Record<GyeokGukCompat['synergy'], string> = {
    '상생흐름': '십신 생극 흐름 (매우 좋음)',
    '상호보완': '같은 계열 상호 이해 (좋음)',
    '중립': '특별한 시너지 없음',
    '충돌': '격국 간 충돌 (주의)',
  };

  const description = `격국: ${gk1.type}(${gk1.status}) ↔ ${gk2.type}(${gk2.status}) — ${synergyLabel[synergy]}`;

  return {
    person1Type: gk1.type,
    person2Type: gk2.type,
    person1Category: gk1.category,
    person2Category: gk2.category,
    person1Status: gk1.status,
    person2Status: gk2.status,
    synergy,
    description,
  };
}
